/**
 * Curriculum metadata. The lesson contents live in Notion; this is just
 * the structural skeleton (levels, ordering, AI-tagged levels).
 *
 * Numbers like 7.5 and 8.5 are intentional — they sort cleanly between
 * 7 and 8 in any UI that orders numerically.
 */

export type LevelMeta = {
  id: number;
  emoji: string;
  title: string;
  shortTitle: string;
  description: string;
  expectedLessons: number;
  audience: 'everyone' | 'power-user';
  flavor?: 'ai' | 'capstone';
};

export const LEVELS: LevelMeta[] = [
  {
    id: 0,
    emoji: '📦',
    title: 'Notion Is Not Magic',
    shortTitle: 'Notion Is Not Magic',
    description: 'Pages, databases, blocks — what they actually are.',
    expectedLessons: 6,
    audience: 'everyone',
  },
  {
    id: 1,
    emoji: '🧭',
    title: "Your First 10 Minutes in Vim's Notion",
    shortTitle: 'First 10 Min',
    description: "Find Teams Directory, find the Wiki, never get lost in the sidebar again.",
    expectedLessons: 8,
    audience: 'everyone',
  },
  {
    id: 2,
    emoji: '📝',
    title: 'Pages, Blocks & Markdown',
    shortTitle: 'Pages & Blocks',
    description: 'Headings, callouts, /commands, mentions.',
    expectedLessons: 10,
    audience: 'everyone',
  },
  {
    id: 3,
    emoji: '🗄️',
    title: 'Databases — How Vim Tracks Things',
    shortTitle: 'Databases',
    description: 'Views, filters, sorts, properties — the People DB, OMT.',
    expectedLessons: 10,
    audience: 'everyone',
  },
  {
    id: 4,
    emoji: '🏢',
    title: 'Find Anything — by Department',
    shortTitle: 'Find by Dept',
    description: 'CEO · Commercial · CTO · CS · Finance · Legal · Marketing · Ops · People · Product · R&D.',
    expectedLessons: 11,
    audience: 'everyone',
  },
  {
    id: 5,
    emoji: '🤝',
    title: 'Working Together',
    shortTitle: 'Working Together',
    description: 'Comments, mentions, permissions, sub-pages.',
    expectedLessons: 8,
    audience: 'everyone',
  },
  {
    id: 6,
    emoji: '📊',
    title: 'Notion as a Daily Driver',
    shortTitle: 'Daily Driver',
    description: 'Templates, recurring docs, meeting notes, your team home.',
    expectedLessons: 8,
    audience: 'everyone',
  },
  {
    id: 7,
    emoji: '🔍',
    title: 'Search & Discover Like a Pro',
    shortTitle: 'Search & Discover',
    description: 'Quick find, AI search, linked databases.',
    expectedLessons: 6,
    audience: 'everyone',
  },
  {
    id: 7.5,
    emoji: '🪄',
    title: 'Notion AI — Page-Level Co-Pilot',
    shortTitle: 'Notion AI',
    description: 'Summarize, draft, translate. The space-bar trick.',
    expectedLessons: 6,
    audience: 'everyone',
    flavor: 'ai',
  },
  {
    id: 8,
    emoji: '⚙️',
    title: 'Power User Patterns',
    shortTitle: 'Power User',
    description: 'Linked views, formulas, rollups, sync blocks, automations.',
    expectedLessons: 8,
    audience: 'power-user',
  },
  {
    id: 8.5,
    emoji: '🤖',
    title: 'Notion Agents — Workspace Teammate',
    shortTitle: 'Notion Agents',
    description: "Delegate, don't just ask. Schedule. Audit.",
    expectedLessons: 6,
    audience: 'everyone',
    flavor: 'ai',
  },
  {
    id: 9,
    emoji: '🎓',
    title: "Build Your Team's Page (Capstone)",
    shortTitle: 'Capstone',
    description: 'From blank to a working dashboard the rest of your team uses.',
    expectedLessons: 6,
    audience: 'everyone',
    flavor: 'capstone',
  },
];

export const TOTAL_LESSONS = LEVELS.reduce((s, l) => s + l.expectedLessons, 0);
// Sanity: should be 93

export const STEP_TYPES = ['read', 'explore', 'quiz', 'fill-in', 'sandbox'] as const;
export type StepType = (typeof STEP_TYPES)[number];

export const STEP_TYPE_EMOJI: Record<StepType, string> = {
  read: '📖',
  explore: '🔍',
  quiz: '❓',
  'fill-in': '✏️',
  sandbox: '▶',
};

/**
 * Notion authoring convention: the emoji at the start of a callout in a
 * lesson body marks where a new step begins. The parser strips the VS-16
 * variation selector (U+FE0F) before looking up here, so authors can use
 * either bare ("▶") or VS-16-suffixed ("▶️") forms — but in practice Notion
 * silently drops bare codepoints that aren't valid emojis on their own (▶
 * is one of those), so author with the VS-16 form to be safe.
 */
export const NOTION_STEP_EMOJI_TO_TYPE: Record<string, StepType> = {
  '📖': 'read',
  '🔍': 'explore',
  '❓': 'quiz',
  '✏️': 'fill-in',
  '▶️': 'sandbox',
};

export const SESSION_COOKIE_NAME = 'session';
export const SESSION_TTL_SECONDS = 60 * 60 * 24; // 24h sliding refresh

export const TIMEZONE_FOR_STREAKS = 'Asia/Jerusalem'; // IST — matches Vim's home

export const RATE_LIMIT_PER_MINUTE = 60;
export const PLAN_GENERATIONS_PER_DAY = 3;
export const QA_POSTS_PER_HOUR = 10;
